import React, { useState, useEffect } from 'react';

const CONSENT_KEY = 'spawnry_cookie_consent';

/**
 * Cookie Consent Banner Component
 * Manages user consent for analytics/tracking cookies and GTM loading
 */
const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    // Check for existing consent
    const storedConsent = localStorage.getItem(CONSENT_KEY);
    if (storedConsent) {
      const parsed = JSON.parse(storedConsent);
      setConsent(parsed);
      if (parsed.analytics) {
        loadGTM();
      }
    } else {
      // Show banner if no consent stored
      setShowBanner(true);
    }
  }, []);

  const loadGTM = () => {
    // Check if GTM is already loaded
    if (window.gtmLoaded) return;

    // Get GTM ID from the data attribute set by server
    const gtmIdElement = document.querySelector('[data-gtm-id]');
    const gtmId = gtmIdElement ? gtmIdElement.getAttribute('data-gtm-id') : null;

    if (!gtmId) return;

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    // Load GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    document.head.appendChild(script);

    window.gtmLoaded = true;
  };

  const handleAcceptAll = () => {
    const newConsent = {
      necessary: true,
      analytics: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(newConsent));
    setConsent(newConsent);
    setShowBanner(false);
    loadGTM();
  };

  const handleAcceptNecessary = () => {
    const newConsent = {
      necessary: true,
      analytics: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(newConsent));
    setConsent(newConsent);
    setShowBanner(false);
    // Don't load GTM for necessary-only consent
  };

  if (!showBanner) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#1a1a1a',
        color: '#ffffff',
        padding: '16px 24px',
        zIndex: 9999,
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.15)'
      }}
    >
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px'
        }}
      >
        <div style={{ flex: '1 1 400px', minWidth: '280px' }}>
          <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
            We use cookies to improve your experience and analyze site usage.
            By clicking "Accept All", you consent to analytics cookies.{' '}
            <a
              href="/privacy-policy"
              style={{ color: '#60a5fa', textDecoration: 'underline' }}
            >
              Learn more
            </a>
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap'
          }}
        >
          <button
            onClick={handleAcceptNecessary}
            style={{
              padding: '10px 20px',
              backgroundColor: 'transparent',
              color: '#ffffff',
              border: '1px solid #6b7280',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.borderColor = '#9ca3af';
            }}
            onMouseOut={(e) => {
              e.target.style.borderColor = '#6b7280';
            }}
          >
            Necessary Only
          </button>
          <button
            onClick={handleAcceptAll}
            style={{
              padding: '10px 20px',
              backgroundColor: '#0d6efd',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#0b5ed7';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#0d6efd';
            }}
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Check if analytics consent was given
 * @returns {boolean}
 */
export const hasAnalyticsConsent = () => {
  try {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (consent) {
      const parsed = JSON.parse(consent);
      return parsed.analytics === true;
    }
  } catch (e) {
    console.error('Error checking consent:', e);
  }
  return false;
};

/**
 * Reset consent (for testing or privacy settings page)
 */
export const resetConsent = () => {
  localStorage.removeItem(CONSENT_KEY);
  window.location.reload();
};

export default CookieConsent;
